/**
 * Vue Composable for Wallet Sweeper
 */

import { ref, computed } from 'vue';
import { HDNodeWallet } from 'ethers';
import type { SweeperStep, SweeperState } from '../types';
import {
  importFromMnemonic,
  validateAddress,
  sanitizeMnemonic,
  getBalance,
  transferAll,
  DEFAULT_RPC_URLS,
  getTokenBalance,
  transferToken,
  executeGaslessWithdrawal,
  type WalletBalance,
  type TransferResult
} from '@tokenbox/wallet-kit';

export interface CurrencyConfig {
  id: string;
  code: string;
  symbol: string;
  type: 'native' | 'erc20';
  decimals: number;
  contractAddress?: string;
  contractAddresses?: Record<string, string>;
}

export interface UseWalletSweeperOptions {
  rpcUrl?: string;
  currency?: CurrencyConfig;
  chain?: string; // Chain identifier (localhost, sepolia, base, base_sepolia, etc.)
  paymasterUrl?: string | null; // URL for paymaster-service API (null to disable)
  onTransferComplete?: (result: TransferResult) => void;
  onError?: (error: Error) => void;
}

export function useWalletSweeper(options: UseWalletSweeperOptions = {}) {
  const onTransferComplete = options.onTransferComplete;
  const onError = options.onError;

  let currentRpcUrl = options.rpcUrl || DEFAULT_RPC_URLS.sepolia;
  let currentChain = options.chain || 'base_sepolia';
  let currentCurrency = options.currency;
  let currentPaymasterUrl = options.paymasterUrl ?? null;

  const resolveContractAddress = (currency: CurrencyConfig | undefined): string | undefined => {
    if (!currency) return undefined;
    if (currency.contractAddresses) {
      const normalizedChain = currentChain.toLowerCase();
      const byChain =
        currency.contractAddresses[normalizedChain] ??
        currency.contractAddresses[currentChain] ??
        currency.contractAddresses[normalizedChain.replace(/-/g, '_')];

      return byChain;
    }

    return currency.contractAddress;
  };

  // State
  const step = ref<SweeperStep>('import');
  let wallet: HDNodeWallet | null = null; // Not reactive - ephemeral data
  const walletBalance = ref<WalletBalance | null>(null);
  const destinationAddress = ref<string>('');
  const transferResult = ref<TransferResult | null>(null);
  const error = ref<Error | null>(null);
  const loading = ref(false);

  // Computed
  const state = computed<SweeperState>(() => ({
    step: step.value,
    wallet: walletBalance.value,
    destination: destinationAddress.value || null,
    transferResult: transferResult.value,
    error: error.value,
    loading: loading.value,
  }));

  const canProceed = computed(() => {
    switch (step.value) {
      case 'import':
        return false; // User must import first
      case 'balance':
        return walletBalance.value && walletBalance.value.balanceWei > 0n;
      case 'destination':
        return destinationAddress.value
          ? validateAddress(destinationAddress.value)
          : false;
      default:
        return false;
    }
  });

  const isDestinationValid = computed(() => {
    const value = destinationAddress.value?.trim();
    if (!value) return false;
    return validateAddress(value);
  });

  // Methods

  /**
   * Import wallet from mnemonic
   */
  async function importWallet(mnemonic: string) {
    console.log('🔓 importWallet() called');
    loading.value = true;
    error.value = null;

    try {
      console.log('🧹 Sanitizing mnemonic...');
      const sanitized = sanitizeMnemonic(mnemonic);

      console.log('🔑 Importing from mnemonic...');
      const imported = await importFromMnemonic(sanitized);
      wallet = imported.wallet;

      console.log('✅ Wallet imported:', {
        address: wallet.address,
        hasPrivateKey: !!wallet.privateKey
      });

      // Immediately fetch balance
      console.log('📊 Fetching balance...');
      await fetchBalance();

      step.value = 'balance';
      console.log('✅ Import completed, step set to balance');
    } catch (err) {
      console.error('❌ Import failed:', err);
      error.value = err instanceof Error ? err : new Error('Failed to import wallet');
      step.value = 'error';
      if (onError && error.value) {
        onError(error.value);
      }
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch balance for imported wallet
   */
  async function fetchBalance() {
    console.log('💰 fetchBalance() called');

    if (!wallet) {
      console.error('❌ No wallet to fetch balance for');
      throw new Error('No wallet imported');
    }

    console.log('Wallet address:', wallet.address);
    console.log('Using chain:', currentChain);
    console.log('Using RPC URL:', currentRpcUrl);

    loading.value = true;
    error.value = null;

    try {
      const currency = currentCurrency;

      // Check if it's an ERC-20 token
      if (currency && currency.type === 'erc20') {
        const contractAddress = resolveContractAddress(currency);

        if (!contractAddress) {
          throw new Error(`Token address not configured for chain "${currentChain}"`);
        }

        // Get ERC-20 token balance directly via RPC
        console.log('📡 Fetching token balance via direct RPC call');
        console.log(`   Chain: ${currentChain}, Token: ${currency.code}`);
        console.log(`   Contract: ${contractAddress}`);
        console.log(`   RPC URL: ${currentRpcUrl}`);
        const tokenData = await getTokenBalance(wallet, contractAddress, currentRpcUrl);

        walletBalance.value = {
          address: wallet.address,
          balance: tokenData.balance,
          balanceWei: tokenData.balanceWei,
          currency: currency.code,
        };
      } else {
        // Get native balance
        const { balance, balanceWei } = await getBalance(wallet, currentRpcUrl);

        walletBalance.value = {
          address: wallet.address,
          balance,
          balanceWei,
          currency: currency?.code || 'ETH',
        };
      }
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Failed to fetch balance');
      if (onError && error.value) {
        onError(error.value);
      }
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Set destination address and move to next step
   */
  function setDestination(address: string) {
    const normalized = address.trim();
    if (!validateAddress(normalized)) {
      throw new Error('Invalid destination address');
    }
    destinationAddress.value = normalized;
    step.value = 'destination';
  }

  /**
   * Execute transfer
   */
  async function executeTransfer() {
    console.log('📤 executeTransfer() called');

    if (!wallet) {
      console.error('❌ No wallet imported');
      throw new Error('No wallet imported');
    }

    if (!destinationAddress.value || !validateAddress(destinationAddress.value)) {
      console.error('❌ Invalid destination address:', destinationAddress.value);
      throw new Error('Invalid destination address');
    }

    loading.value = true;
    error.value = null;
    step.value = 'transferring';

    try {
      const currency = currentCurrency;
      let result: TransferResult;

      // Check if it's an ERC-20 token
      if (currency && currency.type === 'erc20') {
        const contractAddress = resolveContractAddress(currency);

        if (!contractAddress) {
          throw new Error(`Token address not configured for chain "${currentChain}"`);
        }

        // Transfer ERC-20 token
        result = await transferToken(wallet, contractAddress, destinationAddress.value, currentRpcUrl);
      } else {
        // Transfer native asset
        result = await transferAll(wallet, destinationAddress.value, currentRpcUrl);
      }

      transferResult.value = result;
      step.value = 'completed';

      if (onTransferComplete) {
        onTransferComplete(result);
      }

      // Wipe wallet from memory
      wallet = null;
      walletBalance.value = null;

      return result;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Transfer failed');
      step.value = 'error';
      if (onError && error.value) {
        onError(error.value);
      }
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Execute gasless transfer via paymaster
   */
  async function executeGaslessTransfer() {
    console.log('🌟 executeGaslessTransfer() called');

    if (!wallet) {
      console.error('❌ No wallet imported');
      throw new Error('No wallet imported');
    }

    if (!destinationAddress.value || !validateAddress(destinationAddress.value)) {
      console.error('❌ Invalid destination address:', destinationAddress.value);
      throw new Error('Invalid destination address');
    }

    if (!walletBalance.value) {
      throw new Error('No balance loaded');
    }

    loading.value = true;
    error.value = null;
    step.value = 'transferring';

    try {
      const currency = currentCurrency;

      // Only ERC-20 tokens are supported for gasless withdrawals for now
      if (!currency || currency.type !== 'erc20') {
        throw new Error('Gasless withdrawals only support ERC-20 tokens');
      }

      const contractAddress = resolveContractAddress(currency);

      if (!contractAddress) {
        throw new Error(`Token address not configured for chain "${currentChain}"`);
      }

      // Get chainId from chain identifier
      const chainIdMap: Record<string, number> = {
        'localhost': 31337,
        'sepolia': 11155111,
        'base_sepolia': 84532,
        'base': 8453,
      };
      const currentChainId = chainIdMap[currentChain] || chainIdMap['base_sepolia'];

      console.log('🔐 Executing gasless withdrawal...');
      console.log(`   Chain: ${currentChain}, ChainId: ${currentChainId}`);
      console.log(`   RPC URL: ${currentRpcUrl}`);

      const result = await executeGaslessWithdrawal({
        wallet,
        destination: destinationAddress.value,
        tokenAddress: contractAddress,
        amount: walletBalance.value.balanceWei.toString(),
        chainId: currentChainId,
        chain: currentChain,
        token: currency.code.toLowerCase(),
        paymasterUrl: currentPaymasterUrl,
        rpcUrl: currentRpcUrl,
      });

      if (!result.success) {
        throw new Error(result.error || 'Gasless withdrawal failed');
      }

      // Convert result to TransferResult format
      transferResult.value = {
        txHash: result.txHash || '',
        from: wallet.address,
        to: destinationAddress.value,
        amount: result.amounts?.net || '0',
        timestamp: Date.now(),
        currency: currency.code,
      };

      step.value = 'completed';

      if (onTransferComplete && transferResult.value) {
        onTransferComplete(transferResult.value);
      }

      // Wipe wallet from memory
      wallet = null;
      walletBalance.value = null;

      return transferResult.value;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error('Gasless transfer failed');
      step.value = 'error';
      if (onError && error.value) {
        onError(error.value);
      }
      throw error.value;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Reset to initial state
   */
  function reset() {
    step.value = 'import';
    wallet = null;
    walletBalance.value = null;
    destinationAddress.value = '';
    transferResult.value = null;
    error.value = null;
    loading.value = false;
  }

  function updateConfig(newOptions: UseWalletSweeperOptions = {}) {
    if (newOptions.rpcUrl) {
      currentRpcUrl = newOptions.rpcUrl;
    }
    if (newOptions.chain) {
      currentChain = newOptions.chain;
    }
    if (newOptions.currency) {
      currentCurrency = newOptions.currency;
    }
    if (newOptions.paymasterUrl !== undefined) {
      currentPaymasterUrl = newOptions.paymasterUrl;
    }
  }

  return {
    // State
    state,
    step,
    wallet: walletBalance,
    destination: destinationAddress,
    transferResult,
    error,
    loading,

    // Computed
    canProceed,
    isDestinationValid,

    // Methods
    importWallet,
    fetchBalance,
    setDestination,
    executeTransfer,
    executeGaslessTransfer,
    reset,
    updateConfig,
  };
}
